import type { RandomSource } from "./random";

export function shuffled<T>(items: readonly T[], random: RandomSource): T[] {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const swapIndex = random.nextInt(index + 1);
    const current = result[index];
    const replacement = result[swapIndex];
    if (current === undefined || replacement === undefined) {
      throw new Error("Unexpected sparse array while shuffling");
    }
    result[index] = replacement;
    result[swapIndex] = current;
  }
  return result;
}

export function arrangeWithRunLimit<T>(
  items: readonly T[],
  keyOf: (item: T) => string,
  random: RandomSource,
  runLimit = 2,
): T[] {
  if (!Number.isInteger(runLimit) || runLimit < 1) {
    throw new RangeError("runLimit must be a positive integer");
  }

  const groups = new Map<string, T[]>();
  for (const item of shuffled(items, random)) {
    const key = keyOf(item);
    const group = groups.get(key) ?? [];
    group.push(item);
    groups.set(key, group);
  }

  const result: T[] = [];
  let previousKey: string | null = null;
  let runLength = 0;

  while (result.length < items.length) {
    const options = [...groups.entries()].filter(([key, group]) => {
      if (group.length === 0) return false;
      return key !== previousKey || runLength < runLimit;
    });

    if (options.length === 0) {
      throw new Error("Items cannot be arranged within the requested run limit");
    }

    const weighted: Array<readonly [string, T[]]> = [];
    for (const option of options) {
      for (let weight = 0; weight < option[1].length; weight += 1) {
        weighted.push(option);
      }
    }

    const selected = weighted[random.nextInt(weighted.length)];
    if (!selected) throw new Error("Failed to select an arrangement group");
    const [selectedKey, selectedGroup] = selected;
    const item = selectedGroup.pop();
    if (item === undefined) throw new Error("Selected arrangement group was empty");

    result.push(item);
    if (selectedKey === previousKey) {
      runLength += 1;
    } else {
      previousKey = selectedKey;
      runLength = 1;
    }
  }

  return result;
}
